"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { Menu as MenuIcon, X as XIcon, ChevronDown } from "lucide-react";
import { QuoteRequestForm } from "./QuoteRequestForm";

type SelectOption = { value: string; label: string };

export function Header({ hasActiveJobs = false, serviceOptions }: { hasActiveJobs?: boolean; serviceOptions?: SelectOption[] }) {
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<null | "disinfestazione" | "other-services">(null);

  const isActive = (href: string) => {
    if (href === "/") return pathname === "/";
    return pathname.startsWith(href);
  };

  const items = [
    { key: "home", label: "Home", href: "/" },
    { key: "disinfestazione", label: "Disinfestazione", href: "/disinfestazione" },
    { key: "derattizzazione", label: "Derattizzazione", href: "/derattizzazione" },
    { key: "other-services", label: "Altri Servizi", href: "/altri-servizi" },
    { key: "contacts", label: "Contatti", href: "/contatti" },
    ...(hasActiveJobs ? [{ key: "careers", label: "Lavora con Noi", href: "/lavora-con-noi" }] : []),
  ];

  const otherServicesItems = [
    { label: "Pest Management Integrato", href: "/altri-servizi/pest-management" },
    { label: "Sanificazione", href: "/altri-servizi/sanificazione" },
    { label: "Diserbo", href: "/altri-servizi/diserbo" },
    { label: "Allontanamento Volatili", href: "/altri-servizi/allontanamento-volatili" },
  ];

  const disinfestationItems = [
    { label: "Insetti Striscianti", href: "/disinfestazione/striscianti" },
    { label: "Insetti Volanti", href: "/disinfestazione/volanti" },
    { label: "Aracnidi", href: "/disinfestazione/aracnidi" },
    { label: "Infestanti del Legno", href: "/disinfestazione/legno" },
  ];

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="mx-auto max-w-[1320px] px-6 xl:px-8">
        <div className="flex justify-between items-center h-20">
          <Link href="/" className="flex items-center">
            <Image src="/logo.png" alt="Studio Roma Service Logo" width={130} height={50} priority />
          </Link>

          <nav className="hidden lg:flex items-center gap-10 xl:gap-12">
            {items.map((item) => {
              if (item.key !== "other-services" && item.key !== "disinfestazione") {
                return (
                  <Link
                    key={item.key}
                    href={item.href}
                    className={isActive(item.href) ? "text-green-600 font-medium" : "text-gray-700 hover:text-green-600 font-medium"}
                  >
                    {item.label}
                  </Link>
                );
              }

              // Dropdown for Disinfestazione
              if (item.key === "disinfestazione") {
                return (
                  <div
                    key={item.key}
                    className="relative"
                    role="button"
                    tabIndex={0}
                    aria-haspopup="true"
                    aria-expanded={openDropdown === "disinfestazione"}
                    onMouseEnter={() => setOpenDropdown("disinfestazione")}
                    onMouseLeave={() => setOpenDropdown((prev) => (prev === "disinfestazione" ? null : prev))}
                    onFocus={() => setOpenDropdown("disinfestazione")}
                    onBlur={(e) => {
                      // Close only when focus leaves the wrapper entirely
                      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                        setOpenDropdown(null);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setOpenDropdown((prev) => (prev === "disinfestazione" ? null : "disinfestazione"));
                      }
                      if (e.key === "Escape") {
                        setOpenDropdown(null);
                      }
                    }}
                  >
                    <Link
                      href={item.href}
                      className={isActive(item.href) ? "text-green-600 font-medium inline-flex items-center" : "text-gray-700 hover:text-green-600 font-medium inline-flex items-center"}
                    >
                      {item.label}
                      <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${openDropdown === "disinfestazione" ? "rotate-180" : ""}`} />
                    </Link>
                    <div className={`absolute left-0 top-full z-50 ${openDropdown === "disinfestazione" ? "block" : "hidden"}`}>
                      <div className="mt-2 w-80 bg-white rounded-2xl shadow-xl ring-1 ring-black/5 p-2">
                        <ul className="py-1">
                          {disinfestationItems.map((sub) => (
                            <li key={sub.href}>
                              <Link href={sub.href} className="block px-4 py-3 text-gray-800 hover:bg-green-600 hover:text-white rounded-xl">
                                {sub.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              }

              // Dropdown for Altri Servizi
              return (
                <div
                  key={item.key}
                  className="relative"
                  role="button"
                  tabIndex={0}
                  aria-haspopup="true"
                  aria-expanded={openDropdown === "other-services"}
                  onMouseEnter={() => setOpenDropdown("other-services")}
                  onMouseLeave={() => setOpenDropdown((prev) => (prev === "other-services" ? null : prev))}
                  onFocus={() => setOpenDropdown("other-services")}
                  onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                      setOpenDropdown(null);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOpenDropdown((prev) => (prev === "other-services" ? null : "other-services"));
                    }
                    if (e.key === "Escape") {
                      setOpenDropdown(null);
                    }
                  }}
                >
                  <Link
                    href={item.href}
                    className={isActive(item.href) ? "text-green-600 font-medium inline-flex items-center" : "text-gray-700 hover:text-green-600 font-medium inline-flex items-center"}
                  >
                    {item.label}
                    <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${openDropdown === "other-services" ? "rotate-180" : ""}`} />
                  </Link>
                  <div className={`absolute left-0 top-full z-50 ${openDropdown === "other-services" ? "block" : "hidden"}`}>
                    <div className="mt-2 w-80 bg-white rounded-2xl shadow-xl ring-1 ring-black/5 p-2">
                      <ul className="py-1">
                        {otherServicesItems.map((sub) => (
                          <li key={sub.href}>
                            <Link href={sub.href} className="block px-4 py-3 text-gray-800 hover:bg-green-600 hover:text-white rounded-xl">
                              {sub.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </nav>

          <div className="hidden lg:inline-flex">
            <QuoteRequestForm
              showPopupButton
              popupButtonText="Preventivo Gratuito"
              popupButtonClassName="items-center bg-green-600 hover:bg-green-700 text-white px-3 py-2 md:px-4 rounded-md transition-colors"
              serviceOptions={serviceOptions}
            />
          </div>

          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden" aria-label="Open menu">
            {isMobileMenuOpen ? <XIcon /> : <MenuIcon />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden pt-4">
            <div className="border-t border-gray-200 my-4" />
            <div className="px-5">
              {items.map((item) => (
                <Link
                  key={item.key}
                  href={item.href}
                  className={`block py-5 text-2xl font-medium ${isActive(item.href) ? "text-green-600" : "text-gray-800 hover:text-green-600"}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}

              <div className="mt-8 pb-4">
                <QuoteRequestForm
                  showPopupButton
                  popupButtonText="Preventivo Gratuito"
                  popupButtonClassName="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-xl text-lg font-semibold"
                  serviceOptions={serviceOptions}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

